import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { IFormFieldValidation } from '@nibtravel/shared/util/models';
import { v4 as newGuid } from 'uuid';
import { Subject } from 'rxjs';

const defaultHostId = newGuid();

export type InputType =
  | 'text'
  | 'date'
  | 'number'
  | 'email'
  | 'checkbox'
  | 'tel';

@Directive()
export abstract class BaseInputComponent implements ControlValueAccessor {
  protected _trigger$ = new Subject<void>();
  public trigger$ = this._trigger$.asObservable();
  protected _onChange!: (value: any) => void;
  protected _onTouched!: () => void;

  @Input() labelE2eId: string | null = null;

  /** The id of e2e tag */
  @Input() e2eId: string | null = null;

  /** The value of the input element */
  @Input() value: any;

  /**
   * The HTML Id attribute for the host element.
   * default implementation auto generates a unique id
   * if none is provided.
   */
  @Input() hostId!: string;

  /**
   * Had to revert from previous syntax as Jest doesn't understand the
   * default assignment. This property Binds the HTML Id attribute for
   * the host element. Default implementation auto generates a unique id
   * when the input HostId is not provided.
   * */
  @HostBinding('id') get id(): string {
    return this.hostId ? this.hostId : defaultHostId;
  }

  /** The HTML Id attribute for the input element */
  @Input() inputId!: string;

  /** The text for the Label element */
  @Input() label!: string;

  /**
   * There are cases where the label is external to input component.
   * On those cases, the label property will be undefined and the validation message cannot be set
   * based on this property. Use this property to provide a value to be used within the validation msg.
   *
   * By default the validator will use this message and fallback to label property.
   *
   */
  @Input() validationlabel!: string;

  /** The HTML id attribute for the label element */
  @Input() labelId!: string;

  /** The HTML name attribute */
  @Input() inputName!: string;

  /** The HTML placeholder attribute */
  @Input() placeholder!: string;

  /** The HTML disabled attribute */
  @Input() disabled!: boolean;

  /** Override the optional text when field is not defined as required */
  @Input() optional!: string;

  /**
   * Flag to determine whether the input is required or not.
   * By default all the fields will be marked as required since the majority of our use case is like that
   * Hence, if a field is optional, you need to set the flag to false.
   *
   * NOTE: This flag does not append the Required validator! It's sole purpuse is to control
   * the text optional on the component HTML markup.
   */
  @Input() required = true;

  /** Used to set the 'aria-label' attribute on the underlying input element. */
  @Input('aria-label') ariaLabel!: string;

  /** The 'aria-labelledby' attribute takes precedence as the element's text alternative. */
  @Input('aria-labelledby') ariaLabelledby!: string;

  /** The 'aria-describedby' attribute is read after the element's label and field type. */
  @Input('aria-describedby') ariaDescribedby!: string;

  /** The HTML type attribute */
  @Input() type: InputType = 'text';

  /**
   * There are use cases where we need to keep the validation behavior on the field
   * but the message itself to be displayed somewhere else. Use this property combined
   * with the errorMessageComponent
   */
  @Input() errorMessageOnFormGroup = false;

  /** The array of validations applied to the field */
  @Input() validations!: IFormFieldValidation[];

  /** When value is true, the validation will only be triggered if the change value of control is different than the original value. */
  @Input() enableDistinctUntilChanged = true;

  /** The change event, subscribe to the event stream to capture the changes on the input */
  @Output() valueChange = new EventEmitter();

  /** The touched event, subscribe to the event stream to capture when the input has been clicked */
  @Output() touched = new EventEmitter();

  /** The focus event, subscribe to the event stream to capture when the input is focused */
  @Output() focus = new EventEmitter();

  /**
   * The error event, subscribe to the event to receive the error messages
   * Event added to allow message to be rendered in a different position
   * instead of the default implementation. Useful for form groups.
   */
  @Output() error = new EventEmitter();

  get formComponent(): BaseInputComponent {
    return this;
  }

  constructor(
    // Retrieve the dependency only from the local injector,
    // not from parent or ancestors.
    @Self()
    // We want to be able to use the component without a form,
    // so we mark the dependency as optional.
    @Optional()
    public ngControl: NgControl,
    protected cd: ChangeDetectorRef,
    public hostElement: ElementRef,
    protected renderer: Renderer2
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: (value: any) => void): void {
    // Store the provided function as an internal method.
    this._onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: () => void): void {
    // Store the provided function as an internal method.
    this._onTouched = fn;
  }

  /**
   * The implementation of the interface ControlValueAccessor.onChange
   * Is the default function to provide the stream of changes
   */
  onChange(value: any): void {
    this._onChange?.(value);
    this.valueChange.emit(value);
  }

  /**
   * The implementation of the interface ControlValueAccessor.onTouched
   * Is the default function to provide the stream of changes based on click
   */
  onBlur(): void {
    this._onTouched?.();
    this.touched.emit();
    this._trigger$.next();
  }

  /** Default implementation for the onFocus event */
  onFocus(value: any): void {
    this.focus.emit(value);
  }
}
